import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useSEO } from '../../hooks/useSEO';
import { Calculator, MapPin, Home, ArrowRight, TrendingUp, TrendingDown, Minus, BarChart3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => new Intl.NumberFormat('fr-DZ').format(v);

export default function EstimatePage() {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ city: '', neighborhood: '', property_type: 'appartement', surface: 80, rooms: 3, transaction_type: 'vente' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useSEO({
    title: isFr ? 'Estimer mon bien - Beytam' : 'تقييم عقاري - بيتم',
    description: isFr ? 'Estimez gratuitement la valeur de votre bien immobilier en Algérie.' : 'قيّم عقارك مجاناً في الجزائر.',
  });

  useEffect(() => {
    fetch(`${API}/api/price-map`).then(r => r.json()).then(d => setCities(d.cities || []));
  }, []);

  const selectedCity = cities.find(c => c.city === form.city);
  const neighborhoods = selectedCity?.neighborhoods || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/estimate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setResult(await res.json());
    } catch { setResult(null); }
    setLoading(false);
  };

  const types = [
    { v: 'appartement', l: isFr ? 'Appartement' : 'شقة' },
    { v: 'maison', l: isFr ? 'Maison' : 'منزل' },
    { v: 'villa', l: isFr ? 'Villa' : 'فيلا' },
    { v: 'terrain', l: isFr ? 'Terrain' : 'أرض' },
    { v: 'commercial', l: isFr ? 'Local commercial' : 'محل تجاري' },
    { v: 'bureau', l: isFr ? 'Bureau' : 'مكتب' },
  ];

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="estimate-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Calculator className="w-6 h-6 text-emerald-300" />
            <span className="text-emerald-300 text-sm font-medium uppercase tracking-wider">{isFr ? 'Estimation gratuite' : 'تقييم مجاني'}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3" data-testid="estimate-title">
            {isFr ? 'Estimez votre bien immobilier' : 'قيّم عقارك'}
          </h1>
          <p className="text-emerald-200 text-base sm:text-lg max-w-xl mx-auto">
            {isFr ? 'Obtenez une estimation instantanée basée sur les données réelles du marché algérien.' : 'احصل على تقييم فوري بناءً على بيانات السوق الجزائري الحقيقية.'}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-slate-100 mb-8" data-testid="estimate-form">
          {/* Transaction toggle */}
          <div className="flex bg-slate-100 rounded-full p-1 mb-6 w-fit mx-auto" data-testid="estimate-transaction-toggle">
            <button type="button" onClick={() => setForm({ ...form, transaction_type: 'vente' })}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${form.transaction_type === 'vente' ? 'bg-emerald-700 text-white shadow' : 'text-slate-600'}`}
            >{isFr ? 'Vente' : 'بيع'}</button>
            <button type="button" onClick={() => setForm({ ...form, transaction_type: 'location' })}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${form.transaction_type === 'location' ? 'bg-emerald-700 text-white shadow' : 'text-slate-600'}`}
            >{isFr ? 'Location' : 'إيجار'}</button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* City */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{isFr ? 'Ville' : 'المدينة'}</label>
              <select value={form.city} onChange={e => setForm({ ...form, city: e.target.value, neighborhood: '' })}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" data-testid="estimate-city" required>
                <option value="">{isFr ? 'Sélectionnez...' : 'اختر...'}</option>
                {cities.map(c => <option key={c.city} value={c.city}>{isFr ? c.city : c.city_ar}</option>)}
              </select>
            </div>
            {/* Neighborhood */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{isFr ? 'Quartier' : 'الحي'}</label>
              <select value={form.neighborhood} onChange={e => setForm({ ...form, neighborhood: e.target.value })}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" data-testid="estimate-neighborhood">
                <option value="">{isFr ? 'Moyenne de la ville' : 'متوسط المدينة'}</option>
                {neighborhoods.map(n => <option key={n.name} value={n.name}>{isFr ? n.name : n.name_ar || n.name}</option>)}
              </select>
            </div>
            {/* Type */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{isFr ? 'Type de bien' : 'نوع العقار'}</label>
              <select value={form.property_type} onChange={e => setForm({ ...form, property_type: e.target.value })}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" data-testid="estimate-type">
                {types.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            {/* Rooms */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{isFr ? 'Nombre de pièces' : 'عدد الغرف'}</label>
              <select value={form.rooms} onChange={e => setForm({ ...form, rooms: parseInt(e.target.value) })}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" data-testid="estimate-rooms">
                {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            {/* Surface */}
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{isFr ? 'Surface (m²)' : 'المساحة (م²)'}</label>
              <input type="number" min="10" max="10000" value={form.surface} onChange={e => setForm({ ...form, surface: parseFloat(e.target.value) || 0 })}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" data-testid="estimate-surface" required />
            </div>
          </div>

          <button type="submit" disabled={!form.city || loading}
            className="w-full mt-6 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="estimate-submit">
            {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Calculator className="w-5 h-5" />}
            {isFr ? 'Estimer maintenant' : 'قيّم الآن'}
          </button>
        </form>

        {/* Result */}
        {result?.estimated && <EstimateResult result={result} isFr={isFr} />}
      </div>
    </div>
  );
}

function EstimateResult({ result, isFr }) {
  const TrendBadge = ({ trend }) => {
    if (trend === 'up') return <span className="flex items-center gap-1 text-emerald-600 text-xs font-medium"><TrendingUp className="w-3.5 h-3.5" />{isFr ? 'Hausse' : 'صعود'}</span>;
    if (trend === 'down') return <span className="flex items-center gap-1 text-red-500 text-xs font-medium"><TrendingDown className="w-3.5 h-3.5" />{isFr ? 'Baisse' : 'نزول'}</span>;
    return <span className="flex items-center gap-1 text-slate-400 text-xs font-medium"><Minus className="w-3.5 h-3.5" />{isFr ? 'Stable' : 'مستقر'}</span>;
  };

  return (
    <div className="space-y-6 pb-12" data-testid="estimate-result">
      {/* Main result */}
      <div className="bg-white rounded-2xl shadow-lg border border-emerald-100 overflow-hidden">
        <div className="bg-emerald-700 text-white p-6 text-center">
          <p className="text-emerald-200 text-sm mb-1">{isFr ? 'Estimation de votre bien' : 'تقييم عقارك'}</p>
          <p className="text-4xl sm:text-5xl font-bold" data-testid="estimate-total">{fmt(result.estimated_total)} DA</p>
          <p className="text-emerald-200 text-sm mt-2">
            {isFr ? `Fourchette : ${fmt(result.range_low)} — ${fmt(result.range_high)} DA` : `النطاق: ${fmt(result.range_low)} — ${fmt(result.range_high)} دج`}
          </p>
        </div>
        <div className="p-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-xs text-slate-500">{isFr ? 'Prix/m²' : 'سعر/م²'}</p>
            <p className="font-bold text-slate-800">{fmt(result.price_per_sqm)} DA</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500">{isFr ? 'Moy. ville' : 'متوسط المدينة'}</p>
            <p className="font-bold text-slate-800">{fmt(result.city_avg_sqm)} DA</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500">{isFr ? 'Écart / ville' : 'الفرق / المدينة'}</p>
            <p className={`font-bold ${result.diff_vs_city_pct > 0 ? 'text-red-500' : result.diff_vs_city_pct < 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
              {result.diff_vs_city_pct > 0 ? '+' : ''}{result.diff_vs_city_pct}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500">{isFr ? 'Tendance' : 'الاتجاه'}</p>
            <div className="flex justify-center"><TrendBadge trend={result.neighborhood_trend} /></div>
          </div>
        </div>
      </div>

      {/* Neighborhood comparison */}
      {result.neighborhood_prices?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-emerald-700" />
            {isFr ? `Comparaison des quartiers de ${result.city}` : `مقارنة أحياء ${result.city_ar}`}
          </h3>
          <div className="space-y-2">
            {result.neighborhood_prices.map(n => {
              const isSelected = n.name === result.neighborhood;
              const maxPrice = Math.max(...result.neighborhood_prices.map(x => x.price_sqm));
              const pct = (n.price_sqm / maxPrice) * 100;
              return (
                <div key={n.name} className={`flex items-center gap-3 p-2 rounded-lg ${isSelected ? 'bg-emerald-50 border border-emerald-200' : ''}`}>
                  <span className="text-sm font-medium text-slate-700 w-28 shrink-0">{n.name}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                    <div className={`h-full rounded-full ${isSelected ? 'bg-emerald-600' : 'bg-slate-300'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-sm font-bold text-slate-800 w-24 text-right">{fmt(n.price_sqm)} DA</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
